/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React from 'react';
import { ResponsiveLine } from '@nivo/line'; // https://nivo.rocks/line/
import { linearGradientDef } from '@nivo/core'
// redux
import { useSelector, useDispatch } from 'react-redux';
import { updateCurrentPage } from '../../../../../state/slices/app';
// material
import LoadingState from '../../../../../material/states/loading/loading';
import NoDataState from '../../../../../material/states/nodata/nodata';
import CardWrapper from '../../../../../material/cardwrapper/cardwrapper';
import ChangeBadge from '../../../../../material/changebadge/changebadge';
// utils
import { abbreviatedMonths } from '@insticator/insticator-ui';
import { formatNumber } from '../../../../../utils/charts';
// styles
import injectSheet from 'react-jss';
import styles from './chartcard_styles';



/* ========== ~~~~~~~~~~ CHART CARD ~~~~~~~~~~ ========== */
const ChartCard = injectSheet(styles)(props => {
    const dispatch = useDispatch();
    const currentPage = useSelector(state => state.app.currentPage);
    const handleActiveState = () => currentPage.includes(props.route);
    const handleRoute = () => dispatch(updateCurrentPage(props.route));
    const handleClick = () => props.onClick || handleRoute();
    const handleActive = () => props.selected || handleActiveState();

    // old tooltip logic
    const handleCustomTooltip = (dataSlice) => {
        let date = new Date(dataSlice.point.data.x);
        return(
            <div style={{ color: '#bbb' }}>
                <div style={{ background: '#FFFFFF', border: '1px solid rgb(236, 240, 247)', borderRadius: '3px', color: '#062B75', padding: '8px 5px', display: 'flex', flexDirection: 'row'}}>
                    <div style={{marginRight: '10px'}}>{abbreviatedMonths[date.getMonth()]} {date.getDate()}, {date.getFullYear()}</div> <strong>{formatNumber(dataSlice.point.data.y, props.yType)}</strong>
                </div>
            </div>
        )};

    const handleMouseMove = e => {
        props.setHideTooltip(false);
        if(props.currentPosition.x != e.x) {
            props.setCurrentPosition({x: e.x, y:e.y, data: e.data});
        }   
    }

    const handleMouseLeave = () => {
        props.setHideTooltip(true);
    }

    const maxY = Math.max(...props.data[0].data.map(chartData => chartData.y), 0);
    
    return (
        <CardWrapper klass={`chartcard ${props.classes.chartcard}`} isActive={handleActive()} tooltipContent={props.tooltip} handleClick={handleClick}>
            <div className={'titleheading'}>
                <div className={'title'}>{props.title}</div>
                { props.changeType && props.changeType.toLowerCase() !== 'pop' &&
                    <>
                        <ChangeBadge direction={props.direction} change={props.change && !Number.isNaN(props.change) ? formatNumber(props.change, 'percent') : '0.0%'}/>
                        <span className={'changetype'}>{props.changeType}</span>
                    </>
                }
            </div>
            { props.loading && <LoadingState /> }
            { props.noData && <NoDataState title={props.title} variant={'small'} /> }
            {!props.loading && !props.noData && <div className={'value'}>{props.loading ? formatNumber(0, props.yType) : formatNumber(props.value, props.yType)}</div>}
            {!props.loading && !props.noData &&
                <div className={'visualization'} onMouseLeave={handleMouseLeave}>
                    <ResponsiveLine
                        data={props.data}
                        margin={{
                            left: 5,
                            bottom: 5,
                            right: 5,
                            top: 5
                        }}
                        xScale={{
                            type: 'time',
                            format: '%Y-%m-%d',
                            precision: 'day',
                            useUTC: false}}
                        yScale={{
                            "type": "linear",
                            "stacked": false,
                            "max": maxY * 1.25
                        }}
                        axisBottom={null}
                        axisLeft={null}
                        tooltip={slice => !props.isSynched ? handleCustomTooltip(slice) : null}
                        onMouseMove={e => props.isSynched ? handleMouseMove(e) : null}
                        enableGridX={false}
                        enableGridY={false}
                        colors={d => d.color}
                        lineWidth={2}
                        enablePoints={false}
                        pointSize={8}
                        pointColor={'White'}
                        pointBorderWidth={2}
                        pointBorderColor={'#1494f4'}
                        enableArea={true}
                        areaOpacity={.05}
                        fill={[{ match: '*', id: 'gradientA' }]}
                        defs={[
                            linearGradientDef('gradientA', [
                                { offset: 0, color: '#1494f4', opacity: 1 },
                                { offset: 100, color: 'white', opacity: 0 },
                            ]),
                        ]}
                        motionStiffness={90}
                        motionDamping={15}
                        useMesh={true}
                        enableSlices={false} // added for custom line
                        enableCrosshair={true}
                        crosshairType={'x'}
                        // custom layer
                        layers={[
                            'grid', 'markers',  'axes', 'areas', 
                            'lines', 'points', 'slices', 'mesh', 'legends', 
                            d => props.isSynched && !props.hideTooltip ? (<CustomLine d={d} currentPoint={props.currentPosition} yType={props.yType} />) : null, 
                        ]}
                        theme={{
                            crosshair: {
                                line: {
                                    stroke: 'rgb(20, 148, 244)',
                                    strokeWidth: 2,
                                    strokeOpacity: 1,
                                }
                            }
                        }}
                    />
                </div>
            }
        </CardWrapper>
    )
})

// custom line
const CustomLine = (props) => {
    const { currentPoint, yType, d } = props;

    const currentData = d.points.filter(point => point.x === currentPoint.x);

    let isFirstHalf = true;
    let date = ''
    let y = '';
    let formattedY = '';
    let currentY = 0;
    if(currentData && currentData[0]  && currentData[0].data) {
        date = new Date(currentData[0].data.x);
        date = `${abbreviatedMonths[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
        y = currentData[0].data.y
        formattedY = formatNumber(y, yType);
        currentY = currentData[0].y;

        const dataLengthInHalf = d.points.length / 2;
        isFirstHalf = currentData[0].index < dataLengthInHalf || currentData[0].data.x < dataLengthInHalf;
    
    }

    if (currentPoint) {
      return (
        <>
            <path style={{transition: 'all .5s ease'}}
                d={`M ${currentPoint.x} 0 L ${currentPoint.x} 100`}
                stroke={'rgba(6,43,117, 0.5)'}
                strokeWidth={1}
                fill="none"
            />
            <circle style={{transition: 'all .5s ease'}}
                r={4}
                cx={currentPoint.x}
                cy={currentY}
                fill="#ffffff"
                stroke={'#1494f4'}
                strokeWidth={2}
            />
            <g className={'tooltip'}>
                <rect 
                    x={!isFirstHalf ? currentPoint.x - 200 : currentPoint.x + 10} 
                    y={0}
                    width={120 + (formattedY.length < 9 ? formattedY.length * 14 : formattedY.length * 10)} 
                    height="35" 
                    fill="white" 
                    style={{stroke: 'rgb(236, 240, 247)', strokeWidth: 2}}
                />
                <text x={!isFirstHalf ? currentPoint.x - 185 : currentPoint.x + 20}  y="22"  fontWeight={500}>{date}</text>
                <text x={!isFirstHalf ? currentPoint.x - 78 : currentPoint.x + 125} y="22"  fontWeight={'bold'}>{formattedY}</text>
            </g>
        </>
      );
    }
};


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default ChartCard;
